// src/management/NewMember/AddMember.jsx
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  getFirestore,
  doc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import Dialog from "@mui/material/Dialog";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { functions } from "../../firebase";
import CloseButton from "../../components/CloseButton";
import PersonalDetailsForm from "./PersonalDetailsForm";

export default function AddMember({ isOpen, toggle, member, edit = false }) {
  const db = getFirestore();

  const blankState = {
    profilePic: "",
    firstName: "",
    lastName: "",
    cin: "",
    birthday: "",
    phoneNumber: "",
    gender: "male",
    membershipStatus: "",
    role: "member",
    email: "",
    squareCustomerId: null,
    address: {
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      postalCode: "",
    },
  };

  const [memberData, setMemberData] = useState(blankState);

  /* ──────────────────────────────────────────────────────────
     Seed / clear state when dialog toggles
  ────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!isOpen) {
      setMemberData(blankState);
    } else if (edit && member) {
      console.log("[AddMember] Edit mode – member prop:", member);

      setMemberData({
        ...member,
        birthday: member.birthday || "",
        squareCustomerId: member.squareCustomerId || null,
      });
    }
  }, [isOpen, edit, member]);

  /* log every mutation while editing */
  useEffect(() => {
    if (edit) {
      console.log("[AddMember] memberData (edit mode) now:", member.id, memberData);
    }
  }, [memberData, edit]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const { firstName, lastName, email, phoneNumber, birthday, address } =
      memberData;

    if (!firstName || !lastName || !email) {
      alert("First Name, Last Name, and Email are required.");
      return;
    }

    // Normalise phone for Square
    let formattedPhoneNumber = phoneNumber;
    if (phoneNumber.startsWith("0")) {
      formattedPhoneNumber = `+61${phoneNumber.slice(1)}`;
    } else if (!phoneNumber.startsWith("+")) {
      formattedPhoneNumber = `+${phoneNumber}`;
    }

    // dd/mm/yyyy → yyyy-mm-dd for Square
    let formattedBirthday = "";
    if (birthday && /^\d{2}\/\d{2}\/\d{4}$/.test(birthday)) {
      const [dd, mm, yyyy] = birthday.split("/");
      formattedBirthday = `${yyyy}-${mm}-${dd}`;
    }

    try {
      /* ───────────── Square API ───────────── */
      const squarePayload = {
        customerId: memberData.squareCustomerId,
        email,
        firstName,
        lastName,
        phoneNumber: formattedPhoneNumber,
        birthday: formattedBirthday,
        address: {
          addressLine1: address.addressLine1 || "",
          addressLine2: address.addressLine2 || "",
          city: address.city || "",
          state: address.state || "",
          postalCode: address.postalCode || "",
          country: "AU",
        },
      };

      let customerId = memberData.squareCustomerId;

      if (!edit) {
        const createCustomer = httpsCallable(functions, "createSquareCustomer");
        const resp = await createCustomer(squarePayload);
        customerId = resp?.data?.customerId || customerId;

        if (customerId) {
          setMemberData((prev) => ({ ...prev, squareCustomerId: customerId }));
        }
      } else {
        const updateCustomer = httpsCallable(functions, "updateSquareCustomer");
        const upd = await updateCustomer(squarePayload);
        if (!upd?.data?.success) {
          throw new Error(
            upd?.data?.error || "Square customer update failed"
          );
        }
      }

      /* ───────────── Firestore write ───────────── */
      const finalData = {
        ...memberData,
        squareCustomerId: customerId,
        birthday, // keep dd/mm/yyyy in Firestore
      };

      const docId = edit && member?.id ? member.id : customerId;
      if (!docId) {
        throw new Error("No Firestore document id could be determined.");
      }

      console.log(
        "[AddMember] Writing to Firestore – docId:",
        docId,
        "mode:",
        edit ? "update" : "create"
      );

      const ref = doc(db, "members", docId);

      if (edit) {
        await updateDoc(ref, finalData);
      } else {
        await setDoc(ref, finalData);
      }

      toggle();
    } catch (err) {
      console.error("Error processing member:", err);
      alert(
        `Error: ${err.message || "Check the console for details."}`
      );
    }
  };

  return (
    <Dialog open={isOpen} maxWidth="md" fullWidth>
      <CloseButton onClose={toggle} />
      <Container component="main" maxWidth="md">
        <CssBaseline />
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography variant="h4">
            {edit ? "Update" : "Add"} Member
          </Typography>

          <form onSubmit={handleSubmit} style={{ width: "100%" }}>
            <PersonalDetailsForm
              formData={memberData}
              setFormData={setMemberData}
              member={member}
              edit={edit}
            />

            <Box
              sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}
            >
              <Button variant="contained" type="submit">
                Submit
              </Button>
            </Box>
          </form>
        </Box>
      </Container>
    </Dialog>
  );
}

AddMember.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  member: PropTypes.object,
  edit: PropTypes.bool,
};
